import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BetSlip } from '../components/BetSlip';
import { DisputePanel } from '../components/DisputePanel';
import { PoolBar } from '../components/PoolBar';
import { useCurrentHangout, useMyEntry } from '../hooks/useHangout';
import { useEnterFreeForAll, useMarket } from '../hooks/useMarkets';
import { formatTokens } from '../lib/format';
import { newIdempotencyKey } from '../lib/idempotency';
import { KIND_LABELS } from '../schemas/market.schema';
import { getErrorMessage } from '../services/api-client';
import { useAuthStore } from '../stores/authStore';
import { colors, mono } from '../themes/theme';

export function MarketDetail() {
    const { marketId } = useParams<{ marketId: string }>();
    const id = Number(marketId);
    const navigate = useNavigate();
    const session = useAuthStore((s) => s.session);

    const market = useMarket(Number.isInteger(id) && id > 0 ? id : undefined);
    const hangout = useCurrentHangout();
    const entry = useMyEntry(hangout.data?.id);
    const enter = useEnterFreeForAll();

    const [enterKey] = useState(newIdempotencyKey);

    if (market.isPending) return <Typography color="text.secondary">Loading…</Typography>;
    if (market.isError) return <Alert severity="error">{getErrorMessage(market.error)}</Alert>;

    const m = market.data;
    const amResolver = session?.id === m.resolverId;
    const alreadyIn = m.outcomes.some((o) => o.entrantUserId === session?.id);

    return (
        <Stack spacing={1.5}>
            <Button onClick={() => navigate('/')} color="inherit" sx={{ alignSelf: 'flex-start', px: 0 }}>
                ← Board
            </Button>

            <Box>
                <Typography variant="overline" sx={{ color: colors.muted }}>
                    {KIND_LABELS[m.kind]}
                    {m.kind === 'free_for_all' && m.buyIn !== null && ` · ${m.buyIn} to enter`}
                </Typography>
                <Typography variant="h3" sx={{ textTransform: 'none', fontSize: '1.5rem' }}>
                    {m.question}
                </Typography>
                {m.detail && <Typography color="text.secondary">{m.detail}</Typography>}
                <Typography
                    sx={{
                        fontFamily: mono,
                        fontSize: '0.7rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: colors.muted,
                        mt: 0.5,
                    }}
                >
                    {m.creatorHandle} put this up · {m.resolverHandle} decides
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: colors.muted }}>
                {m.kind === 'free_for_all'
                    ? 'Everyone puts in the same amount. Whoever wins takes the lot.'
                    : m.kind === 'closest'
                      ? 'Everyone names a number. Whoever lands closest takes the whole pot.'
                      : 'All the tokens go in one pot. If you back the right side you split the pot with everyone else who did \u2014 so the less popular side pays more.'}
            </Typography>

            <Paper sx={{ p: 1.75 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.2 }}>
                    <Typography sx={{ fontFamily: mono, fontSize: '0.7rem', color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                        {m.bettors} in
                    </Typography>
                    <Typography sx={{ fontFamily: mono, fontSize: '0.7rem', color: colors.safety, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                        Pot {formatTokens(m.pot)}
                    </Typography>
                </Stack>

                {m.kind === 'closest' ? (
                    <Typography sx={{ fontFamily: mono, color: colors.inkSoft }}>
                        {m.resolvedValue !== null
                            ? `Answer: ${m.resolvedValue}`
                            : 'Everyone names a number. Nearest takes the pot.'}
                    </Typography>
                ) : (
                    <PoolBar market={m} />
                )}

                {m.resolvedOutcomeId !== null && (
                    <Typography
                        sx={{ fontFamily: mono, fontSize: '0.75rem', color: colors.win, mt: 1 }}
                    >
                        {m.outcomes.find((o) => o.id === m.resolvedOutcomeId)?.label} won
                    </Typography>
                )}

                {m.status === 'settled' && (
                    <Chip
                        size="small"
                        label="Settled"
                        sx={{ mt: 1, bgcolor: colors.raised, color: colors.win }}
                    />
                )}
                {m.status === 'void' && (
                    <Chip
                        size="small"
                        label="Called off — everyone got their tokens back"
                        sx={{ mt: 1, bgcolor: colors.raised, color: colors.muted }}
                    />
                )}
            </Paper>

            {m.kind === 'free_for_all' ? (
                m.status === 'open' && !alreadyIn ? (
                    <Stack spacing={1}>
                        {enter.isError && <Alert severity="error">{getErrorMessage(enter.error)}</Alert>}
                        <Button
                            variant="contained"
                            size="large"
                            disabled={enter.isPending}
                            onClick={() => enter.mutate({ marketId: m.id, idempotencyKey: enterKey })}
                        >
                            {enter.isPending ? 'Entering…' : `Enter for ${formatTokens(m.buyIn ?? 0)}`}
                        </Button>
                        <Typography variant="body2" sx={{ color: colors.muted }}>
                            Needs {m.minParticipants} in or everyone gets their buy-in back.
                        </Typography>
                    </Stack>
                ) : (
                    alreadyIn &&
                    m.status === 'open' && <Alert severity="success">You&rsquo;re in. Winner takes the pot.</Alert>
                )
            ) : (
                <BetSlip
                    market={m}
                    balance={entry.data?.balance ?? 0}
                    eliminated={entry.data?.eliminatedAt != null}
                />
            )}

            {amResolver && m.status !== 'settled' && m.status !== 'void' && (
                <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate(`/markets/${m.id}/settle`)}
                >
                    Say what happened
                </Button>
            )}

            <DisputePanel
                marketId={m.id}
                canParticipate={m.myStake > 0}
                settled={m.status === 'settled'}
            />

        </Stack>
    );
}
